import React, { useCallback, useMemo } from "react";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { useDashboards } from "../components/Project/Dashboards/hook/api/dashboard";
import { indexBy } from "diagnostics-lib/iterable";
import { useFaultEvents, useRemoteFaultEvents } from "./useFaultEvents";
export function useRemoteFaultEventDashboards(projectId: string) {
  const { body: faultEventDashboards } = useQuery(
    "/projects/:projectId(uuid)/faultEvents/dashboards",
    {
      routeParams: { projectId },
    },
  );
  const getCachedData = useGetQueryData("/projects/:projectId(uuid)/faultEvents/dashboards", {
    routeParams: { projectId },
  });
  const readItems = useCallback(() => getCachedData()?.body, [getCachedData]);
  const fetchUpdatedItems = useCallback(
    (since: string) =>
      call("GET", "/projects/:projectId(uuid)/faultEvents/dashboards/updates/:since(date)", {
        routeParams: { projectId, since: new Date(since) },
      }).then(
        res => [res.body.faultEventDashboards, res.body.deletedFaultEventDashboardIds] as const,
      ),
    [projectId],
  );
  const mutateItems = useMutateBody("/projects/:projectId(uuid)/faultEvents/dashboards", {
    routeParams: { projectId },
  });

  useChannelUpdates(
    { key: "fault_events_dashboards", projectId },
    { fetchUpdatedItems, mutateItems, readItems },
  );

  return faultEventDashboards;
}

export function useDashboardLinkedFaultEvents(projectId: string, dashboardId: string) {
  const faultEvents = useRemoteFaultEvents(projectId);
  const faultEventLinkedDashboards = useRemoteFaultEventDashboards(projectId);

  return useMemo(
    () =>
      faultEventLinkedDashboards
        .filter(link => link.dashboardId === dashboardId)
        .flatMap(link => {
          const id = link.faultEventId;
          return faultEvents.filter(evt => evt.id === id).map(e => ({ ...e, name: e.name }));
        }),
    [dashboardId, faultEventLinkedDashboards, faultEvents],
  );
}

export const useFaultEventsDashboards = (projectId: string) => {
  const faultEventDashboards = useRemoteFaultEventDashboards(projectId);
  const dashboards = useDashboards(projectId).dashboards;
  const faultEvents = useFaultEvents(projectId).events;
  const dashboardsById = indexBy(dashboards, "id");
  const faultEventsById = indexBy(faultEvents, "id");

  return React.useMemo(
    () =>
      faultEventDashboards.flatMap(faultEventDashboard => {
        const faultEvent = faultEventsById.get(faultEventDashboard.faultEventId);
        const dashboard = dashboardsById.get(faultEventDashboard.dashboardId);
        if (dashboard === undefined || faultEvent === undefined) return [];

        return [
          {
            ...faultEventDashboard,
            faultEventName: faultEvent.name,
            name: dashboard.title,
            description: dashboard.description,
            startsAt: faultEvent.startsAt,
            endsAt: faultEvent.endsAt,
            published: dashboard.published,
          },
        ];
      }),
    [faultEventDashboards, dashboardsById, faultEventsById],
  );
};

export type FaultEventDashboard = ReturnType<typeof useFaultEventsDashboards>[number];
